import {
    signatureWidth,
    unitDist,
    unitDistIntervals,
    barlineWidth,
    note16extra,
    clefWidth,
    hiddenClefWidth,
    doClefWidth,
    accidentalMargin,
    accidentalWidth,
} from "../parameters";
import { getLyricWidth } from "../../../../../common/parameters";
import getLastBar from "../../methods/getLastBar";
import { IBar, IBarAccidental, IBarline, INote, IScore, ISettings, ISignature, ISpaces, TBarlineType } from "../../../../../common/types";
import { IAccidental } from "../utils";

interface IProps {
    scoreTotalWidth: number | null,
    score: IScore,
    paddingSide: number,
    signatures: ISignature[],
    barlines: IBarline[],
    scale: number,
    settings: ISettings,
    editor: boolean,
    accidentals: IAccidental[],
    pitchPositions: (number[] | null)[][][],
    intervalsTask?: boolean,
    chords3Task?: boolean,
};

const getSystemBaseWidth = (score: IScore, accidental: number) => {
    const { settings: { staffLines, stick } } = score;
    let systemWidth = 8;
    if (staffLines && !stick && !score.hideClefs) {
        systemWidth += (score.doClef ? doClefWidth : clefWidth) + accidentalMargin;
        if (accidental !== 0 && !score.doClef) {
            systemWidth += accidentalWidth * Math.abs(accidental);
        }
    }
    if (score.hideClefs) {
        systemWidth += hiddenClefWidth;
    }
    return systemWidth;
}

export const getDistribution = (
    {
        scoreTotalWidth,
        score,
        paddingSide,
        signatures,
        barlines,
        scale,
        settings,
        editor,
        accidentals,
        pitchPositions,
        intervalsTask,
        chords3Task,
    }: IProps) => {
    if (scoreTotalWidth === null) return {};
    scoreTotalWidth -= paddingSide * 2 * scale;
    scoreTotalWidth /= scale;
    const { lowerText, lowerText2, noteDistance = 1 } = settings;
    let distribution: number[][] = [[]];
    let startBar = 0;
    if (score.bars[0] === null) startBar = 1;
    const lastBar = getLastBar(score);
    const barSpaces: ISpaces[] = [];
    const barWidths: number[] = [];
    let systemWidth = getSystemBaseWidth(score, accidentals[startBar].i);
    for (let bar = startBar; bar <= lastBar; bar++) {
        let { barWidth, spaces, repeatStartBarlineWidth } = getBarWidth({
            signatures,
            score,
            bar,
            barlines,
            editor,
            pitchPositions,
            lastBar,
            lowerText,
            lowerText2,
            intervalsTask,
            chords3Task,
            noteDistance,
            accidentals,
        });
        barSpaces[bar] = spaces;
        barWidths[bar] = barWidth;
        let systemBreak = isSystemBreak(barlines, bar - 1);
        if (systemWidth + barWidth + repeatStartBarlineWidth <= scoreTotalWidth && !systemBreak) {
            barWidths[bar] += repeatStartBarlineWidth;
            distribution[distribution.length - 1].push(bar);
        } else {
            distribution.push([bar]);
            systemWidth = getSystemBaseWidth(score, (accidentals[bar] || {i : 0}).i);
            if (signatures.findIndex(s => s.bar === bar) === -1) {
                const currentAccidental = accidentals[bar];
                const accidentalDifference = Math.abs(currentAccidental.i) - Math.abs((accidentals[1] || accidentals[0]).i)
                const addition = Math.max(0, signatureWidth - accidentalDifference * accidentalWidth);
                systemWidth += addition;
            }
        }
        systemWidth += barWidth;
    }
    return { distribution, barSpaces, barWidths };
};

export default getDistribution;

export const isSystemBreak = (barlines: IBarline[], bar: number) => {
    let i = barlines.findIndex(barline => barline.bar === bar);
    if (i !== -1) {
        if (barlines[i].break) {
            return true;
        }
    }
    return false;
};

export const isTruncBarline = (barlines: IBarline[], bar: number) => {
    let i = barlines.findIndex(barline => barline.bar === bar);
    if (i !== -1) {
        if (barlines[i].trunc) {
            return true;
        }
    }
    return false;
};

const hasModifier = (note: INote, barAccidentals: IBarAccidental[], pitchPosition: number[]) => {
    if (note.p) {
        let pitches;
        if (Array.isArray(note.p)) {
            pitches = note.p;
        } else {
            pitches = [note.p];
        }
        return (
            pitches.filter(pitch => {
                return pitch.a || (!pitch.a && barAccidentals.findIndex(a => a.tone === pitchPosition[0]) !== -1);
            }).length > 0
        );
    }
    return false;
};

interface IGetBarWidthProps {
    signatures: ISignature[],
    score: IScore,
    bar: number,
    barlines: IBarline[],
    editor: boolean,
    pitchPositions: (number[] | null)[][][],
    lastBar: number,
    lowerText: ISettings["lowerText"],
    lowerText2: ISettings["lowerText2"],
    intervalsTask?: boolean,
    chords3Task?: boolean,
    noteDistance: number,
    accidentals: IAccidental[],
};

export const getBarWidth = ({
    signatures,
    score,
    bar,
    barlines,
    editor,
    pitchPositions,
    lastBar,
    lowerText,
    lowerText2,
    intervalsTask,
    chords3Task,
    noteDistance,
    accidentals,
}: IGetBarWidthProps) => {
    const spaces: {
        [prop: string]: number[];
    } = {};
    let repeatStartBarlineWidth = 0;
    let barWidth = [0, 0, 0, 0, 0, 0];
    if (signatures.findIndex(signature => signature.bar === bar) !== -1) {
        barWidth = barWidth.map(bw => bw + signatureWidth);
    } else {
        for (let b in barWidth) {
            const bars = score["bars" + (+b || "")];
            if (bars && bars[bar] && bars[bar].notes.length === 0 && editor && lastBar === bar) {
                barWidth = barWidth.map(bw => bw + signatureWidth);
                break;
            }
        }
    }

    if (accidentals[bar + 1]?.isVisible) {
        const accidental = accidentals[bar + 1];
        barWidth = barWidth.map(bw => bw + Math.abs(accidental.i) * accidentalWidth + accidentalMargin);
    }

    if (bar - 1 > 0 || (bar === 0 && score.bars[0])) {
        const prevBarline = barlines.find(barline => barline.bar === bar - 1);
        if (
            prevBarline &&
            (prevBarline.type === "doubleRepeatStart" ||
                prevBarline.type === "doubleRepeatBoth" ||
                prevBarline.type === "finalRepeatStart" ||
                prevBarline.type === "finalRepeatBoth")
        ) {
            const type =
                prevBarline.type === "doubleRepeatBoth"
                    ? "doubleRepeatBothStart"
                    : prevBarline.type === "finalRepeatBoth"
                    ? "finalRepeatBothStart"
                    : prevBarline.type;
            repeatStartBarlineWidth += barlineWidth[type];
        }
    }
    let barAccidentals: IBarAccidental[] = [];
    let lastBarPosition = 0;
    for (let voice = 0; voice < 6; voice++) {
        const bars = score["bars" + (voice || "")] as IBar[];
        if (!bars) continue;
        if (!bars[bar]) continue;
        let barPosition = 0;
        let lastNote;
        for (let n = 0; n < bars[bar].notes.length; n++) {
            let note = bars[bar].notes[n];
            if (!lastNote) lastNote = note;
            if (voice === 0) spaces[barPosition] = [0, 0];
            const pitches = Array.isArray(note.p) ? note.p : [note.p];
            pitches.forEach(pitch => {
                if (pitch && (pitch.a === 1 || pitch.a === -1)) {
                    let alreadyHasToneIndex = barAccidentals.findIndex(a => a.tone === pitchPositions[voice][bar][n]![0]);
                    if (alreadyHasToneIndex !== -1) {
                        barAccidentals.splice(alreadyHasToneIndex, 1);
                    }
                    if (pitch.a === 1) {
                        barAccidentals.push({
                            tone: pitchPositions[voice][bar][n]![0],
                            a: 1,
                        });
                    } else if (pitch.a === -1) {
                        barAccidentals.push({
                            tone: pitchPositions[voice][bar][n]![0],
                            a: -1,
                        });
                    }
                }
            });
            let noteWidth =
                (4 / note.l) *
                (note.d ? 1.5 : 1) *
                (intervalsTask || chords3Task ? unitDistIntervals : unitDist) *
                (note.t ? 2 / 3 : 1) *
                noteDistance;
            if (note.l === 16) {
                if (!spaces[barPosition]) spaces[barPosition] = [0, 0];
                spaces[barPosition][0] = note16extra;
            }
            if (note.y && lowerText === 1) {
                if (!spaces[barPosition]) spaces[barPosition] = [0, 0];
                spaces[barPosition][0] = Math.max(
                    spaces[barPosition][0],
                    Math.max(35, getLyricWidth(note.y)) + (note.h ? 6 : 4) - noteWidth
                );
            }
            if (note.y2 && lowerText2 === 1) {
                if (!spaces[barPosition]) spaces[barPosition] = [0, 0];
                spaces[barPosition][0] = Math.max(
                    spaces[barPosition][0],
                    Math.max(35, getLyricWidth(note.y2)) + (note.h2 ? 6 : 4) - noteWidth
                );
            }
            if (n > 0 && lastNote.l === 16 && hasModifier(note, barAccidentals, pitchPositions[voice][bar][n]!)) {
                spaces[lastBarPosition][1] = 12;
            }
            lastBarPosition = barPosition;
            barPosition += (4 / note.l) * (note.d ? 1.5 : 1) * (note.t ? 2 : 3);
            if (
                n === 0 &&
                pitches &&
                pitches.length > 0 &&
                pitches.filter(pitch => pitch && (pitch.a === 1 || pitch.a === -1)).length > 0
            ) {
                spaces["-1"] = [10, 0];
            }
            barWidth[voice] += noteWidth;
            lastNote = note;
        }
    }
    let overallBarWidth: number = Math.max.apply(Math, barWidth);
    overallBarWidth += Object.keys(spaces)
        .map(key => spaces[key])
        .reduce((a, b) => a + b[0] + b[1], 0);
    // overallBarWidth += Object.values(spaces).reduce((a, b) => a + b[0] + b[1], 0);
    let overallSpaces: ISpaces = {};
    Object.keys(spaces).forEach(key => {
        overallSpaces[key] = spaces[key][0] + spaces[key][1];
    });
    if (bar !== lastBar) {
        let type: TBarlineType = "single";
        let barlineIndex = barlines.findIndex(barline => barline.bar === bar);
        if (barlineIndex !== -1 && barlines[barlineIndex].type) type = barlines[barlineIndex].type!;
        overallBarWidth += barlineWidth[type];
    }
    return { barWidth: overallBarWidth, spaces: overallSpaces, repeatStartBarlineWidth };
};
